import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { message, Select, Button } from "antd";
import { EyeOutlined, SyncOutlined } from "@ant-design/icons";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {
  AllCommunityModule,
  ModuleRegistry,
  provideGlobalGridOptions,
} from "ag-grid-community";
import { fetchBrandsBySelectedClients } from "../../core/api/settings";
import { fetchStripeTransactions, syncStripeTransactions } from "../../core/api/data";
import { useSelector } from "react-redux";
import formatDateWithTimezone from "../../utils/dateUtils";
import {dateFilterParams} from "../../utils/dateUtils";

ModuleRegistry.registerModules([AllCommunityModule]);
provideGlobalGridOptions({ theme: "legacy" });

const StripeTransactionsList = ({ onViewDetails, timezone, filters, refreshTrigger }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncInProgress, setSyncInProgress] = useState(false);
  // We'll use brands from filters instead of maintaining local state
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [error, setError] = useState(null);
  
  const selectedClients = useSelector((state) => state.selectedClients);
  
  useEffect(() => {
    if (filters && filters.brands && filters.brands.length > 0) {
      setLoading(true);
      loadTransactions(filters.brands[0], filters);
    } else {
      setLoading(false);
    }
  }, [filters]);
  
  useEffect(() => {
    if (refreshTrigger > 0 && filters && filters.brands && filters.brands.length > 0) {
      setLoading(true);
      loadTransactions(filters.brands[0], filters);
    }
  }, [refreshTrigger]);
  
  const loadTransactions = async (brandId, currentFilters = null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchStripeTransactions(brandId, currentFilters);
      
      if (response.results) {
        setTransactions(response.results);
        
        if (response.last_sync_time) {
          const syncDate = new Date(response.last_sync_time);
          setLastSyncTime(syncDate.toLocaleString());
        }
      } else {
        setTransactions(response);
      }
    } catch (error) {
      console.error("Error fetching Stripe transactions:", error);
      setError("Failed to fetch Stripe transactions. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleSyncTransactions = () => {
    if (!filters || !filters.brands || filters.brands.length === 0) {
      message.warning("Please select a brand first.");
      return;
    }
    
    const brandId = filters.brands[0];
    setSyncInProgress(true);
    message.loading({ content: "Syncing Stripe transactions...", key: "syncMessage", duration: 0 });
    
    syncStripeTransactions(brandId)
      .then(response => {
        message.success({ content: "Stripe transactions synced successfully!", key: "syncMessage" });
        // Refresh transactions after sync
        return fetchStripeTransactions(brandId);
      })
      .then(response => {
        const transactionsData = response.results || response;
        
        if (response.last_sync_time) {
          const syncDate = new Date(response.last_sync_time);
          setLastSyncTime(syncDate.toLocaleString());
        }
        
        setTransactions(transactionsData);
        setSyncInProgress(false);
      })
      .catch(error => {
        console.error("Error syncing Stripe transactions:", error);
        const errorMsg = getErrorMessage(error);
        setError(errorMsg);
        message.error({ content: `Failed to sync Stripe transactions: ${errorMsg}`, key: "syncMessage" });
        setSyncInProgress(false);
      });
  };
  
  // View transaction details handler
  const handleViewDetails = (transaction) => {
    if (onViewDetails && transaction) {
      onViewDetails(transaction);
    }
  };
  
  // Cell renderer for the View Details button
  const ViewDetailsButton = (params) => {
    return (
      <Button 
        icon={<EyeOutlined />} 
        type="primary" 
        size="small"
        onClick={() => handleViewDetails(params.data)}
      >
        View
      </Button>
    );
  };

  // Format currency based on the currency code
  const formatCurrency = (amount, currency = 'USD') => {
    // Convert from cents to dollars for display
    const value = amount / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(value);
  };

  const transactionColumns = [
    {
      headerName: "#",
      field: "index",
      sortable: true,
      filter: true,
      width: 50,
      valueGetter: (params) => {
        return params.node.rowIndex + 1;
      },
    },
    {
      headerName: "Gateway Account Name",
      field: "gateway_account_name",
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: "Transaction ID",
      field: "id",
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: "Charge ID",
      field: "charge_id",
      sortable: true,
      filter: true,
      width: 150,
      valueGetter: (params) => {
        if (!params.data.charge_id) return "N/A";
        return params.data.charge_id;
      },
    },
    {
      headerName: "Date",
      field: "created_date",
      sortable: true,
      filter: 'agDateColumnFilter',  filterParams: dateFilterParams,
      width: 180,
      valueFormatter: (p) => formatDateWithTimezone(p.value, timezone),
    },
    {
      headerName: "Status",
      field: "status",
      sortable: true,
      filter: true,
      width: 120,
    },
    {
      headerName: "Type",
      field: "type",
      sortable: true,
      filter: true,
      width: 120,
    },
    {
      headerName: "Description",
      field: "description",
      sortable: true,
      filter: true,
      width: 200,
      valueGetter: (params) => {
        return params.data.description || "N/A";
      },
    },
    {
      headerName: "Amount",
      field: "amount",
      sortable: true,
      filter: true,
      width: 120,
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return "";
        return formatCurrency(params.value, params.data.currency);
      },
    },
    {
      headerName: "Net Amount",
      field: "net_amount",
      sortable: true,
      filter: true,
      width: 120,
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return "";
        return formatCurrency(params.value, params.data.currency);
      },
    },
    {
      headerName: "Fee",
      field: "fee_amount",
      sortable: true,
      filter: true,
      width: 120,
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return "";
        return formatCurrency(params.value, params.data.currency);
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      sortable: false,
      filter: false,
      width: 120,
      cellRenderer: ViewDetailsButton,
    },
  ];

  // Helper function to extract meaningful error messages
  const getErrorMessage = (error) => {
    if (!error) return "Unknown error occurred";
    
    // Handle Axios errors
    if (error.response) {
      // Server responded with a status code outside of 2xx range
      if (error.response.data && error.response.data.detail) {
        return error.response.data.detail;
      }
      
      if (error.response.status === 404) {
        return "API endpoint not found. Please check if the backend server is properly configured.";
      }
      
      if (error.response.status === 401) {
        return "Authentication failed. Please log in again.";
      }
      
      if (error.response.status === 403) {
        return "You don't have permission to access this resource.";
      }
      
      return `Server error: ${error.response.status} ${error.response.statusText || ''}`;
    } else if (error.request) {
      // Request was made but no response received
      return "No response from server. Please check your network connection.";
    } else if (error.message) {
      // Something else happened in setting up the request
      return error.message;
    }
    
    return "Unknown error occurred";
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="card-title mb-0">Stripe Transactions</h4>
          <div className="d-flex align-items-center">
            {lastSyncTime && (
              <div className="me-3">
                <small>Last synced: {lastSyncTime}</small>
              </div>
            )}
            {/* Brand selection is now handled by filters */}
            <Button
              type="primary"
              icon={<SyncOutlined spin={syncInProgress} />}
              loading={syncInProgress}
              onClick={handleSyncTransactions}
              disabled={!filters?.brands?.length || syncInProgress}
            >
              Sync
            </Button>
          </div>
        </div>
        
        {loading ? (
          <div className="text-center p-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">
            <h5>Error</h5>
            <p>{error}</p>
            <button 
              className="btn btn-outline-danger" 
              onClick={() => {
                setError(null);
                if (filters?.brands?.length > 0) loadTransactions(filters.brands[0], filters);
              }}
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="ag-theme-quartz" style={{ height: 600, width: '100%' }}>
            <AgGridReact
              rowData={transactions}
              columnDefs={transactionColumns}
              pagination={false}
              defaultColDef={{
                flex: 1,
                minWidth: 100,
                resizable: true,
              }}
              sortModel={[{
                colId: 'created_date',
                sort: 'desc'
              }]}
              overlayNoRowsTemplate={
                transactions.length === 0 ? 
                "<div class='p-3'>No transactions found. Try syncing transactions using the Sync button above.</div>" : 
                "<div class='p-3'>No matching records found</div>"
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default StripeTransactionsList;
